import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  FileText,
  Hash,
  Link,
  History,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import { fetchDispatched } from "../../../store/slices/orderSlice";
import PaginationControls from "../../../utils/PaginationControls";
import ChallanPreviewModal from "./ChallanPreviewModal"; // Imported Modal

interface DispatchedHistoryTabProps {
  searchTerm?: string;
  dateRange?: { from: string; to: string };
}

const DispatchedHistoryTab: React.FC<DispatchedHistoryTabProps> = ({
  searchTerm,
  dateRange = { from: "", to: "" },
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { fullyDispatched, paginations, loading } = useSelector(
    (state: RootState) => state.order,
  );
  const [page, setPage] = useState(1);
  const [visibleProductsMap, setVisibleProductsMap] = useState<
    Record<string, number>
  >({});
  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({});

  // Added state for Print Modal
  const [selectedOrderForPrint, setSelectedOrderForPrint] = useState<
    any | null
  >(null);

  const fromDate = dateRange?.from || "";
  const toDate = dateRange?.to || "";

  useEffect(() => {
    dispatch(
      fetchDispatched({
        page,
        search: searchTerm,
        startDate: fromDate,
        endDate: toDate,
      }),
    );
  }, [dispatch, page, searchTerm, fromDate, toDate]);

  const toggleLogs = (orderId: string) => {
    setExpandedLogs((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const pagination = paginations.fullyDispatched;

  return (
    <section>
      {fullyDispatched.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No fully dispatched orders yet
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-4 xl:columns-5 gap-5 space-y-5">
          {fullyDispatched.map((order) => {
            const lastTracking =
              order.trackingDetails?.[order.trackingDetails.length - 1];
            const isLogExpanded = expandedLogs[order._id];
            const createdByAndAuthorSame = Boolean(
              order.createdBy &&
              order.author &&
              (order.createdBy._id === order.author._id ||
                order.createdBy.username === order.author.username),
            );
            const orderTeam = [
              { label: "Created by", person: order.createdBy },
              { label: "Author", person: order.author },
              { label: "Sales rep", person: order.salesRep },
              { label: "CSE", person: order.cse },
            ].filter(
              ({ label, person }) =>
                person?.username &&
                !(label === "Author" && createdByAndAuthorSame),
            );

            return (
              <motion.div
                key={order._id}
                layout
                className="break-inside-avoid mb-5 bg-white rounded-2xl p-6 shadow-xs border border-gray-100 flex flex-col min-h-[480px]"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-start justify-between mb-4 h-[60px]">
                  <div className="flex items-start">
                    <div className="bg-green-50 p-2 rounded-lg">
                      <CheckCircle size={18} className="text-green-600" />
                    </div>
                    <div className="ml-3 overflow-hidden">
                      <h3 className="font-medium text-sm truncate">
                        Order #{order.orderNumber}
                      </h3>
                      <p className="text-xs text-gray-500 truncate max-w-[120px]">
                        {order.customerId?.name ||
                          order.whatsappCustomerId?.customerName ||
                          order.whatsappCustomerId?.productRegistration?.name ||
                          "Unknown Customer"}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {new Date(
                          order.updatedAt || order.createdDate,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {orderTeam.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-x-3 gap-y-1 rounded-md bg-gray-50 px-2 py-1.5">
                    {orderTeam.map(({ label, person }) => (
                      <div
                        key={label}
                        className="flex min-w-0 items-baseline gap-1"
                      >
                        <span className="shrink-0 text-[10px] text-gray-500">
                          {label}:
                        </span>
                        <span className="max-w-[130px] truncate text-[11px] font-medium text-gray-800">
                          {person?.username}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mb-4 min-h-[170px]">
                  {(() => {
                    const limit = visibleProductsMap[order._id] || 5;
                    const items = order.goods || [];
                    return (
                      <div className="space-y-1.5">
                        {items &&
                          items
                            ?.slice(0, limit)
                            .map((item: any, idx: number) => (
                              <div
                                key={item._id || idx}
                                className="flex justify-between items-center p-2 bg-gray-50 rounded-lg text-[11px] h-[30px]"
                              >
                                <span className="font-medium text-gray-800 truncate pr-2">
                                  {item.product?.productName ||
                                    item.rawMaterial?.name ||
                                    "Unknown"}
                                </span>
                                <span className="text-orange-600 font-bold shrink-0">
                                  × {item.quantity} {item.unit || ""}
                                </span>
                              </div>
                            ))}
                        {items.length > limit && (
                          <button
                            onClick={() =>
                              setVisibleProductsMap((prev) => ({
                                ...prev,
                                [order._id]: limit + 5,
                              }))
                            }
                            className="w-full py-1 text-[10px] text-green-700 font-bold bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                          >
                            Show {Math.min(5, items.length - limit)} more
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="flex-1 flex flex-col">
                  <div className="mb-3">
                    <button
                      onClick={() => toggleLogs(order._id)}
                      className="flex items-center justify-between w-full p-2 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
                    >
                      <div className="flex items-center gap-2 text-slate-600">
                        <History size={14} />
                        <span className="text-[11px] font-bold uppercase tracking-wider">
                          Logs
                        </span>
                      </div>
                      {isLogExpanded ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {isLogExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden bg-slate-50/30 rounded-b-lg border-x border-b border-slate-100"
                        >
                          <div className="p-3 space-y-3">
                            {order.assignmentHistory &&
                            order.assignmentHistory.length > 0 ? (
                              order.assignmentHistory.map(
                                (log: any, lIdx: number) => (
                                  <div
                                    key={lIdx}
                                    className="border-l-2 border-indigo-300 pl-3 py-1"
                                  >
                                    <p className="text-[10px] font-bold text-slate-700 mb-1 flex justify-between">
                                      <span>Batch {lIdx + 1}</span>
                                      <span className="text-slate-400 font-normal">
                                        {new Date(
                                          log.assignedAt,
                                        ).toLocaleDateString()}
                                      </span>
                                    </p>
                                    <div className="space-y-1">
                                      {log.items?.map(
                                        (logItem: any, iIdx: number) => (
                                          <div
                                            key={iIdx}
                                            className="flex justify-between text-[10px] text-slate-500 italic"
                                          >
                                            <span className="truncate max-w-[130px]">
                                              {logItem.itemName}
                                            </span>
                                            <span className="font-semibold text-slate-600 shrink-0">
                                              {logItem.quantity}{" "}
                                              {logItem.unit || ""}
                                            </span>
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                ),
                              )
                            ) : (
                              <p className="text-[10px] text-gray-400 text-center italic">
                                No history logs found
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="mt-auto">
                    <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                      {/* Replaced PDFDownloadLink with trigger button */}
                      <button
                        onClick={() => setSelectedOrderForPrint(order)}
                        className="w-full flex items-center justify-center gap-2 py-2 bg-slate-400 text-white rounded-xl text-[10px] font-bold hover:bg-slate-500 transition-all shadow-sm active:scale-95"
                      >
                        <FileText size={12} />
                        Print Challan
                      </button>
                    </div>

                    <div className="min-h-[60px] flex flex-col justify-end">
                      {order.trackingDetails &&
                      order.trackingDetails.length > 0 &&
                      lastTracking ? (
                        <div className="pt-2 border-t border-gray-100 text-xs space-y-1.5">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Mode</span>
                            <span className="font-medium text-[11px]">
                              {lastTracking.transportMode || "N/A"}
                            </span>
                          </div>
                          {lastTracking.trackingLink && (
                            <a
                              href={lastTracking.trackingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 font-medium text-[11px]"
                            >
                              <Link size={12} /> Track Package
                            </a>
                          )}
                          {lastTracking.trackingNumber && (
                            <div className="flex items-center gap-1.5 text-gray-700 text-[11px]">
                              <Hash size={12} />{" "}
                              <span className="font-medium">
                                {lastTracking.trackingNumber}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="pt-2 border-t border-transparent" />
                      )}
                    </div>

                    <div
                      className={`mt-2 text-xs ${order.dispatch === "completed" ? "text-green-600" : "text-yellow-600"} font-semibold italic text-center`}
                    >
                      {order.dispatch === "completed"
                        ? "Fully Dispactched"
                        : "Partially Dispactched"}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {pagination && pagination.pages > 1 && (
        <div className="mt-8 border-t border-gray-200">
          <PaginationControls
            currentPage={page}
            totalPages={pagination.pages}
            onPageChange={setPage}
            isLoading={loading}
            totalItems={pagination.total}
          />
        </div>
      )}

      {/* Added Print Modal Component */}
      <ChallanPreviewModal
        order={selectedOrderForPrint}
        isOpen={!!selectedOrderForPrint}
        onClose={() => setSelectedOrderForPrint(null)}
      />
    </section>
  );
};

export default DispatchedHistoryTab;
